import { defineComponent as _defineComponent } from 'vue'
import { computed, inject } from 'vue'
import { useRouter } from 'vue-router/composables'

import LButton from '@/components/core/Button.vue'
import DropdownMenuItem from '@/components/core/DropdownMenuItem.vue'
import { showMenuSymbol } from '@/components/notification/utils'
import {
  useHideNotification,
  useMarkNotificationAsRead,
} from '@/composables/notification'
import { useContext } from '@/composables/context'
import { tripModule, waypointModule } from '@/store'
import { Note } from '@/types/note'
import {
  Notification,
  NotificationTripAndReferenceRelated,
  isTripRelatedNotification,
  isTripAndReferenceRelatedNotification,
  NotificationWebviewAction,
} from '@/types/notification'
import { formatRelativeTime } from '@/utils/date-utils'
import { highlightTextSections } from '@/utils/strings'
import { NotificationType } from '@/utils/enums'
import { logEvent } from '@/utils/utility-manager'

export interface NotificationMenuItemProps {
  notification: Notification
}

export default /*#__PURE__*/_defineComponent({
  __name: 'NotificationMenuItem',
  props: {
    notification: null
  },
  setup(__props: any) {

const props = __props as {
  notification: Notification
};


const showMenu = inject(showMenuSymbol)

const router = useRouter()
const { $axios } = useContext()
const { mutateAsync: markNotificationAsRead, isLoading: isLoadingMarkAsRead } =
  useMarkNotificationAsRead()
const { mutateAsync: hideNotification, isLoading: isLoadingHide } =
  useHideNotification()

const createdAt = computed(() => {
  return formatRelativeTime(new Date(props.notification.createdAt))
})

function handleNotificationItemClick(notification: Notification) {
  logEvent('update_notification_clicked', {
    type: notification.type,
    tripId: isTripRelatedNotification(notification)
      ? notification.tripId
      : undefined,
    referenceId: isTripAndReferenceRelatedNotification(notification)
      ? notification.reference._id
      : undefined,
    campaign:
      notification.type === NotificationType.WEBVIEW_ACTION
        ? notification.userInfo.campaign
        : undefined,
  })
  if (notification.type !== NotificationType.GENERAL) {
    navigateToSubject(notification)
  }
}

function handleWebviewAction(notification: NotificationWebviewAction) {
  const url = notification.userInfo.webViewURL
  if (url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer')
  } else {
    console.error('Invalid URL protocol for webview action:', url)
  }
}

async function navigateToSubject(notification: Notification) {
  switch (notification.type) {
    case NotificationType.ADDED_WAYPOINT:
      navigateToWaypoint({
        tripId: notification.tripId,
        waypointId: notification.reference._id,
      })
      break
    case NotificationType.ADDED_HIGHLIGHT:
      await navigateToPOI({
        tripId: notification.tripId,
        poiId: notification.reference._id,
      })
      break
    case NotificationType.ADDED_DOCUMENT:
      router.push(
        `/trip/${notification.tripId}/documents/${notification.reference._id}`,
      )
      break
    case NotificationType.ADDED_NOTE:
      router.push(
        `/trip/${notification.tripId}/notes/${notification.reference._id}`,
      )
      break
    case NotificationType.ADDED_NOTE_TO_WAYPOINT:
      handleAddedNoteToWaypoint(notification)
      break
    case NotificationType.ADDED_NOTE_TO_HIGHLIGHT:
      await handleAddedNoteToHighlight(notification)
      break
    case NotificationType.ADDED_EXPENSE:
      router.push(`/trip/${notification.tripId}/expenses`)
      break
    case NotificationType.REMOVED_WAYPOINT:
    case NotificationType.REMOVED_HIGHLIGHT:
      await navigateToTravelplan(notification.tripId)
      break
    case NotificationType.INVITED_ATTENDEE_YOU:
      router.push(`/`)
      break
    case NotificationType.WEBVIEW_ACTION:
      handleWebviewAction(notification)
      break
    default:
      // do nothing
      break
  }

  await markNotificationAsRead(props.notification._id)
  if (showMenu) {
    showMenu.value = false
  }
}

// --------- Handlers for different notification types ---------

async function handleAddedNoteToWaypoint(
  notification: NotificationTripAndReferenceRelated,
) {
  const note = await fetchNote(notification.tripId, notification.reference._id)
  if (!note?.reference) {
    await navigateToTravelplan(notification.tripId)
    return
  }

  await navigateToWaypoint({
    tripId: notification.tripId,
    waypointId: note.reference,
  })
}

async function handleAddedNoteToHighlight(
  notification: NotificationTripAndReferenceRelated,
) {
  const note = await fetchNote(notification.tripId, notification.reference._id)
  if (!note?.reference) {
    await navigateToTravelplan(notification.tripId)
    return
  }

  await navigateToPOI({
    tripId: notification.tripId,
    poiId: note.reference,
  })
}

/*
  A complete navigation to the POI is not possible for now, because the
  navigation is really messed up with several watchers and manueally setting the
  navigation state.
 */
async function navigateToPOI(options: { tripId: string; poiId: string }) {
  const { poiId, tripId } = options

  await tripModule.fetch(tripId)
  const waypoint = await waypointModule.findWaypointByPOI(poiId)

  if (waypoint?._id) {
    await navigateToWaypoint({ tripId, waypointId: waypoint._id })
  } else {
    await navigateToTravelplan(tripId)
  }
}

async function navigateToWaypoint(options: {
  tripId: string
  waypointId: string
}) {
  const { tripId, waypointId } = options

  if (waypointModule.waypoint) {
    waypointModule.setWaypoint({ waypoint: null })
  }

  await tripModule.fetch(tripId)
  await waypointModule.fetch(waypointId)
  router.push(`/trip/${tripId}/waypoints/${waypointId}`)
}

async function navigateToTravelplan(tripId: string) {
  if (waypointModule.waypoint) {
    waypointModule.setWaypoint({ waypoint: null })
  }

  await tripModule.fetch(tripId)
  router.push(`/trip/${tripId}/waypoints`)
}

async function fetchNote(tripId: string, noteId: string) {
  const {
    data: { note },
  } = await $axios.get<{ note: Note }>(`/api/trips/${tripId}/notes/${noteId}`)
  return note
}

return { __sfc: true,props, showMenu, router, $axios, markNotificationAsRead, isLoadingMarkAsRead, hideNotification, isLoadingHide, createdAt, handleNotificationItemClick, handleWebviewAction, navigateToSubject, handleAddedNoteToWaypoint, handleAddedNoteToHighlight, navigateToPOI, navigateToWaypoint, navigateToTravelplan, fetchNote, LButton, DropdownMenuItem, highlightTextSections, NotificationType }
}

})